interface SciFiLoaderProps {
  size?: 'sm' | 'md' | 'lg';
  fullscreen?: boolean;
  text?: string;
  inline?: boolean;
}

const sizeConfig = {
  sm: { container: 40, outerR: 16, innerR: 11, stroke: 2.5 },
  md: { container: 72, outerR: 30, innerR: 21, stroke: 3 },
  lg: { container: 100, outerR: 42, innerR: 30, stroke: 3.5 },
};

const SciFiLoader = ({ size = 'md', fullscreen = false, text, inline = false }: SciFiLoaderProps) => {
  if (inline) {
    return (
      <svg width={16} height={16} viewBox="0 0 16 16" className="inline-block">
        <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="2" />
        <circle
          cx="8" cy="8" r="6" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          strokeDasharray="9.5 28"
          style={{ transformOrigin: 'center', animation: 'scifi-spin 0.8s linear infinite' }}
        />
      </svg>
    );
  }

  const c = sizeConfig[size];
  const cx = c.container / 2;
  const outerCircum = 2 * Math.PI * c.outerR;
  const innerCircum = 2 * Math.PI * c.innerR;
  const uid = `loader-${size}`;

  const loader = (
    <div className="flex flex-col items-center gap-3">
      <div className="relative flex items-center justify-center" style={{ width: c.container, height: c.container }}>
        {/* Glow backdrop */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(circle, hsl(43 58% 52% / 0.12) 0%, transparent 70%)`,
            filter: 'blur(8px)',
          }}
        />

        <svg width={c.container} height={c.container} viewBox={`0 0 ${c.container} ${c.container}`} className="absolute inset-0">
          <defs>
            <linearGradient id={`${uid}-g1`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="hsl(43 70% 65%)" stopOpacity="1" />
              <stop offset="50%" stopColor="hsl(43 58% 52%)" stopOpacity="0.8" />
              <stop offset="100%" stopColor="hsl(43 70% 65%)" stopOpacity="0.1" />
            </linearGradient>
            <linearGradient id={`${uid}-g2`} x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="hsl(43 58% 52%)" stopOpacity="0.9" />
              <stop offset="100%" stopColor="hsl(43 70% 65%)" stopOpacity="0.05" />
            </linearGradient>
            <filter id={`${uid}-glow`}>
              <feGaussianBlur stdDeviation="1.5" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Outer track */}
          <circle cx={cx} cy={cx} r={c.outerR} fill="none" stroke="hsl(43 58% 52% / 0.1)" strokeWidth={c.stroke * 0.6} />
          {/* Inner track */}
          <circle cx={cx} cy={cx} r={c.innerR} fill="none" stroke="hsl(43 58% 52% / 0.08)" strokeWidth={c.stroke * 0.5} />
        </svg>

        {/* Outer spinning arc — pure CSS */}
        <svg
          width={c.container}
          height={c.container}
          viewBox={`0 0 ${c.container} ${c.container}`}
          className="absolute inset-0"
          style={{ animation: 'scifi-spin 1.8s linear infinite' }}
        >
          <circle
            cx={cx} cy={cx} r={c.outerR}
            fill="none"
            stroke={`url(#${uid}-g1)`}
            strokeWidth={c.stroke}
            strokeLinecap="round"
            strokeDasharray={`${outerCircum * 0.35} ${outerCircum * 0.65}`}
            filter={`url(#${uid}-glow)`}
          />
        </svg>

        {/* Inner spinning arc (counter-rotating) — pure CSS */}
        <svg
          width={c.container}
          height={c.container}
          viewBox={`0 0 ${c.container} ${c.container}`}
          className="absolute inset-0"
          style={{ animation: 'scifi-spin-reverse 1.3s linear infinite' }}
        >
          <circle
            cx={cx} cy={cx} r={c.innerR}
            fill="none"
            stroke={`url(#${uid}-g2)`}
            strokeWidth={c.stroke * 0.8}
            strokeLinecap="round"
            strokeDasharray={`${innerCircum * 0.25} ${innerCircum * 0.75}`}
            filter={`url(#${uid}-glow)`}
          />
        </svg>

        {/* Center pulsing dot — pure CSS */}
        <div
          className="rounded-full"
          style={{
            width: c.stroke * 2,
            height: c.stroke * 2,
            background: 'hsl(43 58% 52%)',
            boxShadow: '0 0 8px hsl(43 58% 52% / 0.6), 0 0 20px hsl(43 58% 52% / 0.3)',
            animation: 'scifi-pulse 1.5s ease-in-out infinite',
          }}
        />
      </div>

      {text && (
        <span
          className="text-xs tracking-[0.2em] uppercase text-muted-foreground"
          style={{ animation: 'scifi-text-fade 2s ease-in-out infinite' }}
        >
          {text}
        </span>
      )}
    </div>
  );

  if (fullscreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        {loader}
      </div>
    );
  }

  return loader;
};

export default SciFiLoader;
